import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import styles from "./DocumentsSection.module.css";
import { downloadContractorDocument } from "../../services/contractorsService";
import {
  getContractorPli,
  uploadContractorPli,
  approvePli,
  rejectPli,
  promotePli,
  sendPliReminder,
  requestPli,
  pliStatusBadge,
} from "../../services/pliService";

// Temporarily switches off every action in the PLI panel (request, reminder, upload,
// promote, approve, reject). The panel stays visible and read-only. Flip to true to
// re-enable — no other change needed.
const PLI_ACTIONS_ENABLED = false;
const DISABLED_HINT = "PLI actions are temporarily unavailable.";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

const Badge = ({ text, color }) => (
  <span style={{ background: color, color: "#fff", padding: "2px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
    {text}
  </span>
);

const PublicLiabilityInsuranceSection = ({ contractor }) => {
  const [pli, setPli] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [promote, setPromote] = useState({ source_document_id: "", expiry_date: "", issue_date: "" });
  const [showUpload, setShowUpload] = useState(false);
  const [upload, setUpload] = useState({ file: null, expiry_date: "", issue_date: "" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getContractorPli(contractor.id);
      if (res.success) setPli(res.data);
      else toast.error(res.message || "Failed to load PLI");
    } catch (e) {
      toast.error(e.message || "Failed to load PLI");
    } finally {
      setLoading(false);
    }
  }, [contractor.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        if (okMsg) toast.success(okMsg);
        await load();
      } else if (res.rateLimited || res.status === 429 || res.hasOpenTicket || res.status === 409) {
        toast.warn(res.message || "That action isn't available right now.");
        await load();
      } else {
        toast.error(res.message || "Action failed");
      }
      return res;
    } catch (e) {
      toast.error(e.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const handleReminder = () => run(() => sendPliReminder(contractor.id), "Reminder sent");
  const handleRequest = () => run(() => requestPli(contractor.id), "PLI request sent");
  const handleApprove = (docId) => run(() => approvePli(contractor.id, docId), "Certificate approved");
  const handleReject = (docId) => {
    const reason = window.prompt("Reason for rejecting this certificate:");
    if (!reason) return;
    return run(() => rejectPli(contractor.id, docId, reason), "Certificate rejected — contractor notified");
  };
  const handlePromote = () => {
    if (!promote.source_document_id || !promote.expiry_date) return toast.warn("Pick a document and enter its expiry date");
    return run(() => promotePli(contractor.id, promote), "Document recorded as the PLI certificate")
      .then(() => { setShowPromote(false); setPromote({ source_document_id: "", expiry_date: "", issue_date: "" }); });
  };
  const handleUpload = () => {
    if (!upload.file) return toast.warn("Choose a certificate file");
    // Keep in step with the backend (config/multer.js uploadPliCertificate): PDF/JPG/PNG, 10MB.
    const okTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!okTypes.includes(upload.file.type)) {
      return toast.warn("Please upload the certificate as a PDF, JPG or PNG.");
    }
    if (upload.file.size > 10 * 1024 * 1024) {
      return toast.warn("That file is larger than 10MB. Please upload a smaller file.");
    }
    if (!upload.expiry_date) return toast.warn("Enter the certificate expiry date");
    const fd = new FormData();
    fd.append("document", upload.file);
    fd.append("expiry_date", upload.expiry_date);
    if (upload.issue_date) fd.append("issue_date", upload.issue_date);
    fd.append("name", "PLI Certificate");
    return run(() => uploadContractorPli(contractor.id, fd), "Certificate uploaded and recorded")
      .then(() => { setShowUpload(false); setUpload({ file: null, expiry_date: "", issue_date: "" }); });
  };

  const handleDownload = async (docId, e) => {
    e?.preventDefault();
    try {
      const res = await downloadContractorDocument(contractor.id, docId);
      if (!res?.success) toast.error(res?.message || "Download failed");
    } catch (err) { toast.error(err.message || "Download failed"); }
  };

  const status = pli?.status;
  const badge = pliStatusBadge(status);
  const emailedToday = pli?.pli_email_sent_today;
  const openTicketId = pli?.open_ticket_id;
  const docs = contractor.documents || [];
  const off = !PLI_ACTIONS_ENABLED;

  return (
    <div className={styles.colMd12}>
      <div className={styles.panelTitle}>
        <h3>Public Liability Insurance</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* status-conditional primary action */}
          {status === "missing" && (
            <button className={styles.btnSecondary}
              disabled={off || busy || emailedToday || !!openTicketId}
              title={
                off
                  ? DISABLED_HINT
                  : openTicketId
                  ? `A PLI ticket (#${openTicketId}) is already open for this contractor — complete it before sending another request`
                  : emailedToday
                  ? "Emailed today — try again tomorrow"
                  : ""
              }
              onClick={handleRequest}>Send PLI request</button>
          )}
          {(status === "due_soon" || status === "lapsed") && (
            <button className={styles.btnSecondary} disabled={off || busy || emailedToday}
              title={off ? DISABLED_HINT : emailedToday ? "Emailed today — try again tomorrow" : ""}
              onClick={handleReminder}>Send reminder now</button>
          )}
          {(contractor.documents || []).length > 0 && (
            <button className={styles.btnSecondary} disabled={off || busy}
              title={off ? DISABLED_HINT : ""}
              onClick={() => { setShowPromote((s) => !s); setShowUpload(false); }}>
              Use existing document
            </button>
          )}
          <button className={styles.btnSecondary} disabled={off || busy}
            title={off ? DISABLED_HINT : ""}
            onClick={() => { setShowUpload((s) => !s); setShowPromote(false); }}>
            Upload certificate
          </button>
        </div>
      </div>

      <div className={styles.panelGroup}>
        {loading ? (
          <p>Loading…</p>
        ) : (
          <div style={{ padding: 8 }}>
            {/* status line */}
            <p style={{ marginBottom: 12 }}>
              <strong>Status:</strong> <Badge {...badge} />
              {pli?.overdue && <>&nbsp;<Badge text="Overdue" color="#d33" /></>}
              &nbsp;&nbsp;<strong>Expiry:</strong> {fmt(pli?.pli_expiry_date)}
            </p>

            {/* current certificate */}
            {pli?.current_certificate ? (
              <p>
                <strong>Current certificate:</strong>{" "}
                <a href="#download" onClick={(e) => handleDownload(pli.current_certificate.id, e)}>
                  {pli.current_certificate.name || pli.current_certificate.file}
                </a>{" "}
                (expires {fmt(pli.current_certificate.date_expiration)})
              </p>
            ) : (
              <p style={{ color: "#888" }}>No active certificate on file.</p>
            )}

            {/* pending review */}
            {pli?.pending_certificate && (
              <div style={{ margin: "12px 0", padding: 12, background: "#fff8e1", borderRadius: 4 }}>
                <strong>Pending review:</strong>{" "}
                <a href="#download" onClick={(e) => handleDownload(pli.pending_certificate.id, e)}>
                  {pli.pending_certificate.name || pli.pending_certificate.file}
                </a>{" "}
                (expires {fmt(pli.pending_certificate.date_expiration)})
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <button className={styles.btnSecondary} disabled={off || busy} title={off ? DISABLED_HINT : ""} onClick={() => handleApprove(pli.pending_certificate.id)}>Approve</button>
                  <button className={styles.btnSecondary} disabled={off || busy} title={off ? DISABLED_HINT : ""} onClick={() => handleReject(pli.pending_certificate.id)}>Reject</button>
                </div>
              </div>
            )}

            {/* upload a new certificate (inline form with real date pickers) */}
            {showUpload && (
              <div style={{ marginTop: 12, padding: 12, background: "#f5f7fa", borderRadius: 4 }}>
                <p style={{ marginTop: 0 }}><strong>Upload a PLI certificate</strong></p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setUpload((u) => ({ ...u, file: e.target.files?.[0] || null }))} />
                  <label style={{ margin: 0 }}>Expiry *</label>
                  <input type="date" value={upload.expiry_date}
                    onChange={(e) => setUpload((u) => ({ ...u, expiry_date: e.target.value }))} />
                  <label style={{ margin: 0 }}>Issue</label>
                  <input type="date" value={upload.issue_date}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setUpload((u) => ({ ...u, issue_date: e.target.value }))} />
                  <button className={styles.btnSecondary} disabled={off || busy} title={off ? DISABLED_HINT : ""} onClick={handleUpload}>Upload</button>
                </div>
              </div>
            )}

            {/* promote existing document */}
            {showPromote && (
              <div style={{ marginTop: 12, padding: 12, background: "#f5f7fa", borderRadius: 4 }}>
                <p style={{ marginTop: 0 }}><strong>Use an existing document as the PLI certificate</strong></p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select value={promote.source_document_id}
                    onChange={(e) => setPromote((p) => ({ ...p, source_document_id: e.target.value }))}>
                    <option value="">— pick a document —</option>
                    {docs.map((d) => <option key={d.id} value={d.id}>{d.name || d.file}</option>)}
                  </select>
                  <label style={{ margin: 0 }}>Expiry</label>
                  <input type="date" value={promote.expiry_date}
                    onChange={(e) => setPromote((p) => ({ ...p, expiry_date: e.target.value }))} />
                  <label style={{ margin: 0 }}>Issue</label>
                  <input type="date" value={promote.issue_date}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setPromote((p) => ({ ...p, issue_date: e.target.value }))} />
                  <button className={styles.btnSecondary} disabled={off || busy} title={off ? DISABLED_HINT : ""} onClick={handlePromote}>Record as PLI</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicLiabilityInsuranceSection;
